import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

function HoverableNavLink({ as: Comp = "a", style = {}, hoverColor = "#d50000", children, ...props }) {
  const [hover, setHover] = useState(false);
  return (
    <Comp
      {...props}
      style={{
        ...style,
        color: hover ? hoverColor : style.color || "#fff",
        cursor: "pointer",
        transition: "color 0.18s"
      }}
      onMouseEnter={(e) => { setHover(true); if (props.onMouseEnter) props.onMouseEnter(e); }}
      onMouseLeave={(e) => { setHover(false); if (props.onMouseLeave) props.onMouseLeave(e); }}
    >
      {children}
    </Comp>
  );
}

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [aboutOpen, setAboutOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [forceMobile, setForceMobile] = useState(false);

  const NAVBAR_HEIGHT = 80;

  // Refs for measuring widths
  const containerRef = useRef(null);
  const menuRef = useRef(null);

  // Responsive: show hamburger if items don't fit
  useEffect(() => {
    function checkOverflow() {
      if (
        containerRef.current &&
        menuRef.current &&
        menuRef.current.scrollWidth > containerRef.current.offsetWidth - 380 // leave room for logo+padding
      ) {
        setForceMobile(true);
      } else if (window.innerWidth < 900) {
        setForceMobile(true);
      } else {
        setForceMobile(false);
      }
    }
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, []);

  // Scroll to a section anchor (offset for navbar height)
  const scrollToAnchor = (anchor) => {
    setTimeout(() => {
      const el = document.querySelector(anchor);
      if (el) {
        const y = el.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: y - NAVBAR_HEIGHT + 2, behavior: "smooth" });
      }
    }, 50);
  };

  // Navigation logic for scroll links
  const handleNavToSection = (e, anchor) => {
    e.preventDefault();
    setMenuOpen(false);

    if (location.pathname === "/") {
      scrollToAnchor(anchor);
    } else {
      navigate("/", { state: { anchor } });
    }
  };

  // Home click: if on home, scroll to top, else navigate home
  const handleHomeClick = (e) => {
    e.preventDefault();
    setMenuOpen(false);

    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  // About Us Dropdown logic
  const handleAboutClick = (e) => {
    e.preventDefault();
    if (forceMobile) setAboutOpen((open) => !open);
  };
  const closeDropdown = () => setAboutOpen(false);

  const navLinks = (
    <>
      <li>
        <HoverableNavLink as={Link} to="/" onClick={handleHomeClick} style={{ fontSize: 16 }}>
          Home
        </HoverableNavLink>
      </li>
      <li>
        <HoverableNavLink
          href="#overview"
          style={{ fontSize: 16 }}
          onClick={e => handleNavToSection(e, "#overview")}
        >
          Results
        </HoverableNavLink>
      </li>
      <li>
        <HoverableNavLink
          href="#video"
          style={{ fontSize: 16 }}
          onClick={e => handleNavToSection(e, "#detail")}
        >
          Event Details
        </HoverableNavLink>
      </li>
      <li>
        <HoverableNavLink
          href="#this-month-events"
          style={{ fontSize: 16 }}
          onClick={e => handleNavToSection(e, "#this-month-events")}
        >
          Calendar
        </HoverableNavLink>
      </li>
      <li>
        <HoverableNavLink
          href="#speakers"
          style={{ fontSize: 16 }}
          onClick={e => handleNavToSection(e, "#speakers")}
        >
          Gallery
        </HoverableNavLink>
      </li>
      <li>
        <HoverableNavLink
          href="#register"
          style={{ fontSize: 16 }}
          onClick={e => handleNavToSection(e, "#register")}
        >
          Online Entry
        </HoverableNavLink>
      </li>
      <li>
        <HoverableNavLink href="#contact" style={{ fontSize: 16 }}>
          Contact
        </HoverableNavLink>
      </li>
      {/* ABOUT US DROPDOWN */}
      <li
        className={`dropdown${aboutOpen ? " open" : ""}`}
        style={{
          position: "relative",
          height: "100%",
          display: "flex",
          alignItems: "center",
        }}
        onMouseEnter={() => !forceMobile && setAboutOpen(true)}
        onMouseLeave={() => !forceMobile && setAboutOpen(false)}
      >
        <HoverableNavLink
          href="#about"
          style={{
            fontSize: 16,
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
          onClick={handleAboutClick}
          aria-haspopup={true}
          aria-expanded={aboutOpen}
        >
          About Us <span style={{ fontSize: "0.7em", marginLeft: 4 }}>▼</span>
        </HoverableNavLink>
        <ul
          className="dropdown-menu"
          style={{
            display: aboutOpen ? "block" : "none",
            position: "absolute",
            top: "100%",
            left: 0,
            minWidth: "180px",
            background: "#222",
            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            padding: "0.5em 0",
            zIndex: 9999,
            borderRadius: "0 0 8px 8px",
            margin: 0,
          }}
        >
          <li>
            <HoverableNavLink as={Link} to="/management" onClick={() => { closeDropdown(); setMenuOpen(false); }} style={{ padding: "8px 24px", display: "block" }}>
              Management
            </HoverableNavLink>
          </li>
          <li>
            <HoverableNavLink as={Link} to="/about-vbkp" onClick={() => { closeDropdown(); setMenuOpen(false); }} style={{ padding: "8px 24px", display: "block" }}>
              About VBKP
            </HoverableNavLink>
          </li>
          <li>
            <HoverableNavLink as={Link} to="/affiliated-units" onClick={() => { closeDropdown(); setMenuOpen(false); }} style={{ padding: "8px 24px", display: "block" }}>
              Affiliated Units
            </HoverableNavLink>
          </li>
        </ul>
      </li>
    </>
  );

  return (
    <div
      className="navbar navbar-fixed-top custom-navbar navbar-bg-solid"
      role="navigation"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: NAVBAR_HEIGHT,
        minHeight: NAVBAR_HEIGHT,
        padding: 0,
        margin: 0,
        display: "flex",
        alignItems: "center",
        background: "#232323",
        boxSizing: "border-box",
        border: 0,
        width: "100vw",
        zIndex: 99,
        boxShadow: "0 2px 6px rgba(0,0,0,0.08)"
      }}
    >
      <div
        className="container"
        ref={containerRef}
        style={{
          display: "flex",
          alignItems: "center",
          height: "100%",
          minHeight: NAVBAR_HEIGHT,
          width: "100%",
          padding: 0,
          margin: 0,
          maxWidth: "100vw",
        }}
      >
        <div
          className="navbar-header"
          style={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            padding: 0,
            margin: 0,
          }}
        >
          <Link
            to="/"
            className="navbar-brand"
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              padding: "0 20px 0 0",
              margin: 0,
              boxSizing: "border-box",
            }}
            onClick={handleHomeClick}
          >
            <img
              src="/images/vbkplogo.png"
              alt="VBKP Logo"
              style={{
                height: "70%",
                width: "auto",
                display: "block",
                objectFit: "contain",
                margin: 0,
                padding: 0,
                boxSizing: "border-box",
                maxHeight: NAVBAR_HEIGHT,
                lineHeight: 0,
                verticalAlign: "middle",
              }}
            />
          </Link>
        </div>

        {/* Hamburger icon */}
        {forceMobile ? (
          <div
            style={{
              marginLeft: "auto",
              marginRight: 16,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              height: NAVBAR_HEIGHT,
              zIndex: 101,
            }}
            onClick={() => setMenuOpen((prev) => !prev)}
          >
            <div style={{
              width: 34, height: 34, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center"
            }}>
              {[0, 1, 2].map((i) => (
                <div key={i} style={{
                  width: 28,
                  height: 4,
                  background: "#fff",
                  borderRadius: 2,
                  margin: "3px 0"
                }} />
              ))}
            </div>
          </div>
        ) : (
          // Desktop Nav
          <div
            className="collapse navbar-collapse"
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              flex: 1,
              justifyContent: "flex-end",
              overflow: "visible",
              width: "auto",
            }}
          >
            <ul
              className="nav navbar-nav navbar-right"
              ref={menuRef}
              style={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                margin: 0,
                padding: 0,
                listStyle: "none",
                background: "transparent",
                gap: 28,
              }}
            >
              {navLinks}
            </ul>
          </div>
        )}
      </div>

      {/* Mobile Menu Drawer */}
      {forceMobile && menuOpen && (
        <div
          style={{
            position: "fixed",
            top: NAVBAR_HEIGHT,
            right: 0,
            width: "80vw",
            maxWidth: 340,
            background: "#232323",
            boxShadow: "-2px 0 16px rgba(0,0,0,0.30)",
            zIndex: 2000,
            transition: "transform 0.2s",
            padding: "30px 0",
            height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
            overflowY: "auto",
            borderRadius: "0 0 0 14px"
          }}
        >
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "flex",
              flexDirection: "column",
              gap: 18,
            }}
          >
            {/* Map all nav items except About Us dropdown (the last one) */}
            {React.Children.toArray(navLinks.props.children).slice(0, -1).map((item) =>
              React.cloneElement(item, {
                style: { ...item.props.style, padding: "6px 32px", fontSize: 18 },
                onClick: (e) => {
                  if (item.props.onClick) item.props.onClick(e);
                  setMenuOpen(false);
                }
              })
            )}
            {/* Now render About Us dropdown manually */}
            <li
              style={{
                position: "relative",
                height: "100%",
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
                width: "100%",
              }}
            >
              <a
                href="#about"
                style={{
                  fontSize: 18,
                  color: "#fff",
                  padding: "6px 32px",
                  width: "100%",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  transition: "color 0.18s"
                }}
                onClick={(e) => {
                  e.preventDefault();
                  setAboutOpen((prev) => !prev);
                }}
              >
                About Us <span style={{ fontSize: "0.7em", marginLeft: 4 }}>▼</span>
              </a>
              {aboutOpen && (
                <ul
                  style={{
                    width: "100%",
                    background: "#222",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                    borderRadius: "0 0 8px 8px",
                    margin: 0,
                    padding: 0,
                    listStyle: "none",
                  }}
                >
                  <li>
                    <HoverableNavLink as={Link} to="/management" onClick={() => { closeDropdown(); setMenuOpen(false); }} style={{ padding: "10px 40px", display: "block" }}>
                      Management
                    </HoverableNavLink>
                  </li>
                  <li>
                    <HoverableNavLink as={Link} to="/about-vbkp" onClick={() => { closeDropdown(); setMenuOpen(false); }} style={{ padding: "10px 40px", display: "block" }}>
                      About VBKP
                    </HoverableNavLink>
                  </li>
                  <li>
                    <HoverableNavLink as={Link} to="/affiliated-units" onClick={() => { closeDropdown(); setMenuOpen(false); }} style={{ padding: "10px 40px", display: "block" }}>
                      Affiliated Units
                    </HoverableNavLink>
                  </li>
                </ul>
              )}
            </li>
          </ul>
        </div>
      )}

      {/* Overlay to close mobile drawer */}
      {forceMobile && menuOpen && (
        <div
          onClick={() => setMenuOpen(false)}
          style={{
            position: "fixed",
            top: NAVBAR_HEIGHT,
            left: 0,
            width: "100vw",
            height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
            background: "rgba(0,0,0,0.25)",
            zIndex: 999,
          }}
        />
      )}
    </div>
  );
}
